(() => {
  const oneDay = 86400000;
  const copyDate = (date) => new Date(date.getTime());
  const addDay = (date) => new Date(date.getTime() + oneDay);
  const key = (date) => date.toISOString().slice(0, 10);
  const monthDay = (date) => key(date).slice(5);
  const dateFromParts = (parts) => new Date(Date.UTC(parts.year, parts.month - 1, parts.day));

  const zonedParts = (date, timeZone) => {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
    });
    const values = Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]));
    return {
      year: Number(values.year), month: Number(values.month), day: Number(values.day),
      hour: Number(values.hour), minute: Number(values.minute)
    };
  };

  const holidaySet = (value) => new Set(value.split(',').map((item) => item.trim()).filter(Boolean));
  const weekdaySet = (value) => new Set(value.split(',').map(Number));
  const isWorking = (date, weekdays, holidays) =>
    weekdays.has(date.getUTCDay()) && !holidays.has(key(date)) && !holidays.has(monthDay(date));

  const nextWorking = (date, weekdays, holidays, includeDate) => {
    let cursor = includeDate ? copyDate(date) : addDay(date);
    for (let i = 0; i < 400; i += 1) {
      if (isWorking(cursor, weekdays, holidays)) return cursor;
      cursor = addDay(cursor);
    }
    return cursor;
  };

  const addWorking = (date, count, weekdays, holidays) => {
    let cursor = copyDate(date);
    for (let i = 0; i < count; i += 1) cursor = nextWorking(cursor, weekdays, holidays, false);
    return cursor;
  };

  const shortDate = (date) => new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC', month: 'short', day: '2-digit'
  }).format(date);

  const range = (from, to) => {
    if (key(from) === key(to)) return shortDate(from);
    if (from.getUTCFullYear() === to.getUTCFullYear() && from.getUTCMonth() === to.getUTCMonth()) {
      return `${shortDate(from)} - ${String(to.getUTCDate()).padStart(2, '0')}`;
    }
    return `${shortDate(from)} - ${shortDate(to)}`;
  };

  const longDate = (date) => {
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${date.getUTCFullYear()}`;
  };

  const setTime = (element, label, date) => {
    if (!element) return;
    element.textContent = label;
    element.dateTime = key(date);
  };

  const setMessage = (element, template, values) => {
    if (!element) return;
    const fragments = template.split(/(\{counter\}|\{delivery_from\}|\{delivery_to\})/g);
    element.replaceChildren();
    fragments.forEach((fragment) => {
      if (!Object.prototype.hasOwnProperty.call(values, fragment)) {
        element.append(document.createTextNode(fragment));
        return;
      }
      const emphasis = document.createElement('strong');
      emphasis.className = fragment === '{counter}'
        ? 'drpply-estimated-delivery__counter'
        : 'drpply-estimated-delivery__date-emphasis';
      emphasis.textContent = values[fragment];
      element.append(emphasis);
    });
  };

  const render = (widget) => {
    const now = new Date();
    const timeZone = widget.dataset.timeZone || 'Asia/Kolkata';
    const parts = zonedParts(now, timeZone);
    const today = dateFromParts(parts);
    const cutoffHour = Number(widget.dataset.cutoffHour);
    const afterCutoff = parts.hour >= cutoffHour;
    const cutoffDate = afterCutoff ? addDay(today) : today;
    const cutoffUTC = Date.UTC(cutoffDate.getUTCFullYear(), cutoffDate.getUTCMonth(), cutoffDate.getUTCDate(), cutoffHour - 5, -30);
    const remainingMinutes = Math.max(0, Math.floor((cutoffUTC - now.getTime()) / 60000));
    const counter = `${String(Math.floor(remainingMinutes / 60)).padStart(2, '0')}${widget.dataset.counterHours} ${String(remainingMinutes % 60).padStart(2, '0')}${widget.dataset.counterMinutes}`;

    const processingDays = weekdaySet(widget.dataset.processingDays);
    const transitDays = weekdaySet(widget.dataset.transitDays);
    const processingHolidays = holidaySet(widget.dataset.processingHolidays);
    const transitHolidays = holidaySet(widget.dataset.transitHolidays);
    const processingStart = nextWorking(today, processingDays, processingHolidays, !afterCutoff);
    const processingFrom = addWorking(processingStart, Number(widget.dataset.processingMin), processingDays, processingHolidays);
    const processingTo = addWorking(processingStart, Number(widget.dataset.processingMax), processingDays, processingHolidays);
    const deliveryFrom = addWorking(processingTo, Number(widget.dataset.transitMin), transitDays, transitHolidays);
    const deliveryTo = addWorking(processingTo, Number(widget.dataset.transitMax), transitDays, transitHolidays);

    setMessage(widget.querySelector('[data-delivery-message]'), widget.dataset.message, {
      '{counter}': counter,
      '{delivery_from}': longDate(deliveryFrom),
      '{delivery_to}': longDate(deliveryTo)
    });
    setTime(widget.querySelector('[data-delivery-purchased-date]'), shortDate(today), today);
    setTime(widget.querySelector('[data-delivery-processing-date]'), range(processingFrom, processingTo), processingFrom);
    setTime(widget.querySelector('[data-delivery-delivered-date]'), range(deliveryFrom, deliveryTo), deliveryFrom);
  };

  const init = () => {
    document.querySelectorAll('[data-drpply-delivery]').forEach((widget) => {
      render(widget);
      if (widget.dataset.drpplyDeliveryReady) return;
      widget.dataset.drpplyDeliveryReady = 'true';
      window.setInterval(() => render(widget), 60000);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
